import React, { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { fmtEuroCents } from "@/lib/format";
import { toast } from "@/components/ui/sonner";
import { Search, Gavel, Trophy, PackageCheck, Send, Loader2, Mail } from "lucide-react";

export default function Bidding() {
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [qDebounced, setQDebounced] = useState("");
  const [cat, setCat] = useState("all");
  const [qtys, setQtys] = useState({});
  const [basketPV, setBasketPV] = useState({});

  useEffect(() => {
    const t = setTimeout(() => setQDebounced(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const { data, isLoading } = useQuery({
    queryKey: ["bidding", cat, qDebounced],
    queryFn: async () =>
      (await api.get(`/procurement/bidding?category=${encodeURIComponent(cat)}&q=${encodeURIComponent(qDebounced)}`)).data,
    placeholderData: keepPreviousData,
  });

  const vendors = data?.vendors || [];
  const rows = data?.rows || [];

  const invalidate = () => qc.invalidateQueries({ queryKey: ["bidding"] });

  const toggleOffer = useMutation({
    mutationFn: async (r) =>
      (await api.post("/procurement/bidding/needs-offer", { item_id: r.item_id, needs_offer: !r.needs_offer })).data,
    onSuccess: invalidate,
    onError: (e) => toast.error(apiErr(e)),
  });

  const setInvite = useMutation({
    mutationFn: async ({ item_id, vendor_ids }) =>
      (await api.put("/procurement/bidding/invite", { item_id, vendor_ids })).data,
    onSuccess: invalidate,
    onError: (e) => toast.error(apiErr(e)),
  });

  const setPreferred = useMutation({
    mutationFn: async ({ item_id, vendor_id }) =>
      (await api.post("/procurement/bidding/preferred", { item_id, vendor_id })).data,
    onSuccess: () => { invalidate(); toast.success("Ο προμηθευτής ορίστηκε"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const generate = useMutation({
    mutationFn: async (lines) =>
      (await api.post("/procurement/bidding/generate-orders", { lines })).data,
    onSuccess: (d) => {
      setQtys({});
      qc.invalidateQueries({ queryKey: ["orders"] });
      toast.success(d.orders === 1 ? "Δημιουργήθηκε 1 παραγγελία (πρόχειρη)" : `Δημιουργήθηκαν ${d.orders} παραγγελίες (πρόχειρες)`);
      if (d.skipped?.length) toast.warning(`Παραλείφθηκαν ${d.skipped.length} είδη χωρίς προμηθευτή`);
    },
    onError: (e) => toast.error(apiErr(e)),
  });

  const sendRfqs = useMutation({
    mutationFn: async () => (await api.post("/procurement/bidding/send-rfqs")).data,
    onSuccess: (d) => toast.success(`Στάλθηκαν αιτήματα προσφοράς σε ${d.sent_to}/${d.total_vendors} προμηθευτές`),
    onError: (e) => toast.error(apiErr(e)),
  });

  const toggleVendor = (r, vid) => {
    const cur = new Set(r.offer_vendor_ids || []);
    cur.has(vid) ? cur.delete(vid) : cur.add(vid);
    setInvite.mutate({ item_id: r.item_id, vendor_ids: [...cur] });
  };

  const orderLines = useMemo(
    () => Object.entries(qtys).filter(([, v]) => Number(v) > 0).map(([item_id, v]) => ({ item_id, qty: Number(v) })),
    [qtys]
  );
  const vendorsInBasket = useMemo(() => {
    const ids = new Set();
    orderLines.forEach((l) => { if (basketPV[l.item_id]) ids.add(basketPV[l.item_id]); });
    return ids.size;
  }, [orderLines, basketPV]);

  const tabCls = (active) =>
    `shrink-0 rounded-full px-3 py-1 text-[11px] font-semibold whitespace-nowrap transition-colors ${
      active ? "bg-[#45423C] text-white" : "bg-[#F1EDE6] text-[#6F6A60] hover:bg-[#E7E1D7]"}`;

  return (
    <div className="space-y-6" data-testid="bidding-page">
      <div>
        <p className="text-xs font-mono uppercase tracking-widest text-[#C86235]">Προμήθειες</p>
        <h1 className="font-serif text-3xl md:text-4xl text-[#45423C] mt-1">Διαγωνισμός</h1>
        <p className="text-[#6F6A60] text-sm mt-1">
          Δες προσφορές & καλύτερη τιμή ανά είδος, ανάθεσε σε ποιους προμηθευτές ζητάμε προσφορά και όρισε τον νικητή.
          Μετά συμπλήρωσε ποσότητες και δημιούργησε παραγγελίες ομαδοποιημένες ανά προμηθευτή.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="h-4 w-4 text-[#6F6A60] absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="bid-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Αναζήτηση είδους"
            className="rounded-xl border border-[#E3DED4] bg-white pl-9 pr-3 py-2 text-sm outline-none focus:border-[#45423C]" />
        </div>
        <button data-testid="bid-send-rfqs" onClick={() => { if (window.confirm("Αποστολή αιτημάτων προσφοράς σε όλους τους προμηθευτές που έχουν ανατεθεί σε είδη;")) sendRfqs.mutate(); }} disabled={sendRfqs.isPending}
          className="inline-flex items-center gap-1.5 rounded-xl border border-[#E3DED4] bg-white px-3 py-2 text-sm font-semibold hover:border-[#B9AE9A] disabled:opacity-50">
          <Mail className="h-4 w-4" /> Αποστολή αιτημάτων προσφοράς
        </button>
        <span className="text-sm text-[#6F6A60] ml-auto">{data?.count || 0} {(data?.count || 0) === 1 ? "είδος" : "είδη"}</span>
      </div>

      <div className="flex flex-wrap items-center gap-1.5" data-testid="bid-cat-tabs">
        <button data-testid="bid-cat-all" onClick={() => setCat("all")} className={tabCls(cat === "all")}>Όλες</button>
        {(data?.by_category || []).map((c) => (
          <button key={c.category} data-testid={`bid-cat-${c.category}`} onClick={() => setCat(c.category)} className={tabCls(cat === c.category)}>
            {c.category} ({c.count})
          </button>
        ))}
      </div>

      <div className="rounded-2xl bg-white border border-[#E3DED4] overflow-hidden">
        <div className="px-5 py-4 border-b border-[#E3DED4] flex items-center gap-2">
          <Gavel className="h-4 w-4 text-[#C86235]" />
          <h2 className="font-medium text-[#45423C]">Είδη προς διαγωνισμό</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#45423C] text-left text-[10px] uppercase tracking-wider text-white/80">
                <th className="px-4 py-3">Είδος</th>
                <th className="px-3 py-3 text-center">Ζητά προσφορά</th>
                <th className="px-3 py-3">Προμηθευτές διαγωνισμού</th>
                <th className="px-3 py-3 text-center">Προσφορές</th>
                <th className="px-3 py-3 text-right">Καλύτερη τιμή</th>
                <th className="px-3 py-3">Προτιμώμενος / Νικητής</th>
                <th className="px-3 py-3 text-right">Ποσότητα</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={7} className="px-4 py-8 text-center text-[#6F6A60]">Φόρτωση…</td></tr>}
              {rows.map((r) => (
                <tr key={r.item_id} data-testid={`bid-row-${r.item_id}`} className="border-t border-[#E3DED4] hover:bg-[#F7F5F1] align-top">
                  <td className="px-4 py-3 font-medium text-[#45423C]">
                    {r.item_name}
                    <div className="text-[10px] text-[#9A948A] uppercase tracking-wide">{r.category}</div>
                  </td>
                  <td className="px-3 py-3 text-center">
                    <input type="checkbox" data-testid={`bid-needs-${r.item_id}`} checked={r.needs_offer}
                      onChange={() => toggleOffer.mutate(r)} className="cursor-pointer accent-[#C86235] h-4 w-4" />
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex flex-wrap gap-1 max-w-[260px]">
                      {vendors.map((v) => {
                        const on = (r.offer_vendor_ids || []).includes(v.id);
                        return (
                          <button key={v.id} data-testid={`bid-invite-${r.item_id}-${v.id}`} onClick={() => toggleVendor(r, v.id)} title={v.name}
                            className={`max-w-[130px] truncate rounded-full px-2 py-0.5 text-[10px] font-semibold border transition-colors ${
                              on ? "bg-[#45423C] text-white border-[#45423C]" : "bg-white text-[#6F6A60] border-[#E3DED4] hover:border-[#B9AE9A]"}`}>
                            {v.name}
                          </button>
                        );
                      })}
                      {vendors.length === 0 && <span className="text-xs text-[#9A948A]">—</span>}
                    </div>
                  </td>
                  <td className="px-3 py-3 text-center tabular">{r.offer_count}</td>
                  <td className="px-3 py-3 text-right tabular font-semibold">
                    {r.best_price === null ? "—" : fmtEuroCents(r.best_price)}
                    {r.best_vendor && <div className="text-[10px] text-[#6E7A63] font-normal flex items-center justify-end gap-0.5"><Trophy className="h-3 w-3" />{r.best_vendor}</div>}
                  </td>
                  <td className="px-3 py-3">
                    <select data-testid={`bid-preferred-${r.item_id}`} value={r.preferred_vendor_id || ""}
                      onChange={(e) => setPreferred.mutate({ item_id: r.item_id, vendor_id: e.target.value })}
                      className={`w-full min-w-[160px] rounded-xl border px-2 py-1.5 text-sm outline-none focus:border-[#45423C] ${
                        r.preferred_vendor_id ? "border-[#4A7A3E] bg-[#F1F4EE]" : "border-[#E3DED4] bg-white"}`}>
                      <option value="">— κανένας —</option>
                      {(r.offers || []).map((o) => (
                        <option key={o.vendor_id} value={o.vendor_id}>
                          {o.vendor_name} · {fmtEuroCents(o.unit_price)}{o.rank === 1 ? " ★" : ""}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-3 text-right">
                    <input data-testid={`bid-qty-${r.item_id}`} type="number" step="0.01" min="0"
                      value={qtys[r.item_id] ?? ""} placeholder="—" disabled={!r.preferred_vendor_id}
                      onChange={(e) => { setQtys({ ...qtys, [r.item_id]: e.target.value }); setBasketPV((m) => ({ ...m, [r.item_id]: r.preferred_vendor_id })); }}
                      title={r.preferred_vendor_id ? "" : "Όρισε πρώτα προμηθευτή"}
                      className="w-20 rounded-lg border border-[#E3DED4] px-2 py-1 text-right text-sm tabular outline-none focus:border-[#45423C] disabled:bg-[#F7F5F1] disabled:cursor-not-allowed" />
                  </td>
                </tr>
              ))}
              {!isLoading && rows.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-[#6F6A60]">Δεν βρέθηκαν είδη.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {orderLines.length > 0 && (
        <div className="sticky bottom-4 z-20 rounded-2xl bg-[#45423C] text-white shadow-xl px-5 py-4 flex flex-wrap items-center gap-4" data-testid="bid-basket">
          <PackageCheck className="h-5 w-5 text-[#E3B23C]" />
          <div className="text-sm">
            <strong className="tabular">{orderLines.length}</strong> {orderLines.length === 1 ? "είδος" : "είδη"} · ομαδοποίηση σε <strong className="tabular">{vendorsInBasket}</strong> {vendorsInBasket === 1 ? "παραγγελία" : "παραγγελίες"}
          </div>
          <button data-testid="bid-generate" onClick={() => generate.mutate(orderLines)} disabled={generate.isPending}
            className="ml-auto inline-flex items-center gap-2 rounded-xl bg-[#C86235] px-5 py-2.5 text-sm font-semibold hover:opacity-90 disabled:opacity-50">
            {generate.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Δημιουργία παραγγελιών
          </button>
        </div>
      )}
    </div>
  );
}
